import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, defineMessages } from 'react-intl';
import classNames from 'classnames';

import intlShape from '@/shared/utils/intlShape';
import api from '@/shared/api';
import config from '@/config';
import AuthService from '@/shared/services/AuthService';
import DocumentService from '@/shared/services/DocumentService';
import handleHttpError from '@/shared/services/HandleHttpError';
import ToastrService from '@/shared/services/ToastrService';
import FileIcon from '@/shared/components/FileIcon';
import YesNoModal from '@/shared/components/YesNoModal';
import DocumentDetailsModal from '@/modules/documents/details/DocumentDetailsModal';

const messages = defineMessages({
  UploadDocument: 'Upload document',
  DeleteDocument: 'Delete document',
  ConfirmDeleteDocument: 'Are you sure you want to delete this document?',
  ConfirmDeleteDocumentTitle: 'Delete?',
});

class DocumentsDropdown extends Component {
  state = {
    documents: [],
    loaded: false,
    uploading: false,
    previewDocument: null,
    deleteDocument: null,
  };

  fileInputRef = React.createRef();

  componentDidMount() {
    this.loadDocuments();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.linkId !== this.props.linkId || prevProps.linkType !== this.props.linkType) {
      this.loadDocuments();
    }
  }

  loadDocuments = () => {
    const { linkId, linkType, bypassPermissionsForTrainingCertificate, intl } = this.props;
    if (!linkId || !linkType) return;
    if (
      !bypassPermissionsForTrainingCertificate &&
      !AuthService.hasPermission({ functionality: 'Documents', privilege: 'Read' })
    )
      return;

    api.base
      .post(`${config.appUrls.documents}/api/documents/GetLinks/${linkType}`, [linkId])
      .then(({ data }) => {
        let docs = [];
        if (data) {
          if (Array.isArray(data)) {
            docs = data;
          } else {
            const targetKey = String(linkId).toLowerCase();
            const match = Object.keys(data).find(k => k.toLowerCase() === targetKey);
            docs = match ? data[match] : [];
          }
        }
        this.setState({ documents: docs || [], loaded: true });
      })
      .catch(handleHttpError(intl));
  };

  handleFileSelect = e => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    this.uploadFiles(files);
    e.target.value = '';
  };

  uploadFiles = files => {
    const { linkId, linkType, relationId, documentType, informQueues, intl } = this.props;
    if (!linkId || !linkType) return;

    const formData = new FormData();
    formData.append('documentLinkType', linkType);
    formData.append('linkId', linkId);
    if (relationId) formData.append('relationId', relationId);
    files.forEach(file => formData.append('data', file));

    const typeParam = documentType != null ? documentType : -1;
    const queryParams = [`documentType=${typeParam}`];
    if (informQueues === false) {
      queryParams.push('informQueues=false');
      queryParams.push('recognitionStatus=4');
    }

    this.setState({ uploading: true });
    api.base
      .post(`${config.appUrls.documents}/api/documents?${queryParams.join('&')}`, formData, {
        headers: { 'Content-Type': undefined },
      })
      .then(() => {
        ToastrService.success();
        this.loadDocuments();
        if (this.props.onUploadComplete) this.props.onUploadComplete();
      })
      .catch(handleHttpError(intl))
      .finally(() => this.setState({ uploading: false }));
  };

  previewDocument = document => {
    this.setState({ previewDocument: document });
  };

  onPreviewSaved = updated => {
    this.setState(state => ({
      previewDocument: null,
      documents: state.documents.map(x => (x.id === updated.id ? { ...x, ...updated } : x)),
    }));
  };

  confirmDelete = document => {
    this.setState({ deleteDocument: document });
  };

  deleteDocument = () => {
    const { linkId, linkType, intl } = this.props;
    const { deleteDocument } = this.state;
    if (!deleteDocument) return;

    api.base
      .delete(
        `${config.appUrls.documents}/api/documents/Links/${deleteDocument.id}/${linkId}/${linkType}`,
      )
      .then(() => {
        this.setState(state => ({
          documents: state.documents.filter(x => x.id !== deleteDocument.id),
          deleteDocument: null,
        }));
        ToastrService.success();
        if (this.props.onUploadComplete) this.props.onUploadComplete();
      })
      .catch(error => {
        handleHttpError(intl)(error);
        this.setState({ deleteDocument: null });
      });
  };

  render() {
    const {
      showUpload,
      readOnly,
      downloadOnClick,
      bypassPermissionsForTrainingCertificate,
      className,
      intl,
    } = this.props;
    const { documents, uploading, previewDocument, deleteDocument } = this.state;

    const canCreateDocuments =
      !readOnly &&
      (bypassPermissionsForTrainingCertificate ||
        AuthService.hasPermission({
          functionality: 'Documents',
          privilege: 'Create',
        }));
    const canReadDocuments =
      bypassPermissionsForTrainingCertificate ||
      AuthService.hasPermission({
        functionality: 'Documents',
        privilege: 'Read',
      });
    const canDeleteDocuments =
      !readOnly &&
      (bypassPermissionsForTrainingCertificate ||
        AuthService.hasPermission({
          functionality: 'Documents',
          privilege: 'Delete',
        }));

    const hasDocuments = documents && documents.length > 0;
    if (!hasDocuments && !(canCreateDocuments && showUpload)) return null;

    return (
      <div
        className={classNames('dropdown documents-dropdown', className)}
        style={{ display: 'inline-block' }}
      >
        <a
          data-toggle="dropdown"
          className="btn btn-xs btn-icon toggle-button"
          aria-haspopup="true"
        >
          <i className="fa fa-paperclip" />
          {hasDocuments && <span className="badge">{documents.length}</span>}
          {!hasDocuments && <span className="caret" />}
        </a>
        <ul className="dropdown-menu dropdown-menu-right">
          {canCreateDocuments && showUpload && (
            <li className="flex-menu-item">
              <a onClick={() => this.fileInputRef.current && this.fileInputRef.current.click()}>
                <i
                  className={classNames('fa fa-fw', {
                    'fa-upload': !uploading,
                    'fa-spinner fa-spin': uploading,
                  })}
                />
                <span className="flex-item">{intl.formatMessage(messages.UploadDocument)}</span>
              </a>
              <input
                ref={this.fileInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={this.handleFileSelect}
              />
            </li>
          )}
          {canCreateDocuments && showUpload && hasDocuments && (
            <li role="separator" className="divider" />
          )}
          {documents.map(document => (
            <li key={document.id} className="flex-menu-item">
              <a
                onClick={
                  canReadDocuments
                    ? e => {
                        if (downloadOnClick) {
                          e.preventDefault();
                          e.stopPropagation();
                          DocumentService.download(document.id);
                        } else {
                          this.previewDocument(document);
                        }
                      }
                    : undefined
                }
                title={document.name}
              >
                <FileIcon document={document} />
                <span className="flex-item margin5L margin5R">{document.name}</span>
                {canDeleteDocuments && (
                  <i
                    className="fa fa-trash clickable margin5L"
                    title={intl.formatMessage(messages.DeleteDocument)}
                    onClick={e => {
                      e.stopPropagation();
                      this.confirmDelete(document);
                    }}
                  />
                )}
              </a>
            </li>
          ))}
        </ul>

        {previewDocument && (
          <DocumentDetailsModal
            show
            documentId={previewDocument.id}
            onSave={this.onPreviewSaved}
            onClose={() => this.setState({ previewDocument: null })}
            onReplaceDocument={() => {}}
          />
        )}

        {deleteDocument && (
          <YesNoModal
            show
            title={intl.formatMessage(messages.ConfirmDeleteDocumentTitle)}
            message={intl.formatMessage(messages.ConfirmDeleteDocument)}
            onNo={() => this.setState({ deleteDocument: null })}
            onSave={this.deleteDocument}
          />
        )}
      </div>
    );
  }
}

DocumentsDropdown.propTypes = {
  linkId: PropTypes.string.isRequired,
  linkType: PropTypes.number.isRequired,
  showUpload: PropTypes.bool,
  readOnly: PropTypes.bool,
  downloadOnClick: PropTypes.bool,
  bypassPermissionsForTrainingCertificate: PropTypes.bool,
  documentType: PropTypes.number,
  relationId: PropTypes.string,
  informQueues: PropTypes.bool,
  onUploadComplete: PropTypes.func,
  className: PropTypes.string,
  intl: intlShape.isRequired,
};

DocumentsDropdown.defaultProps = {
  showUpload: true,
  readOnly: false,
  downloadOnClick: false,
  bypassPermissionsForTrainingCertificate: false,
  documentType: -1,
  relationId: undefined,
  informQueues: true,
  onUploadComplete: undefined,
  className: '',
};

export default injectIntl(DocumentsDropdown);
